import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';

@Component({
  selector: 'app-pagination',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <nav class="flex flex-wrap items-center justify-center gap-[0.375rem]" aria-label="Paginação">
      <button
        type="button"
        class="evo-pagination-btn"
        [disabled]="currentPage <= 1"
        aria-label="Página anterior"
        (click)="previousPage()"
      >
        <svg class="h-4 w-4" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M15.41 7.41 14 6l-6 6 6 6 1.41-1.41L10.83 12z" />
        </svg>
      </button>

      <button
        *ngFor="let page of renderedPages"
        type="button"
        class="evo-pagination-btn"
        [class.evo-pagination-active]="page === currentPage"
        [attr.aria-current]="page === currentPage ? 'page' : null"
        (click)="setPage(page)"
      >
        {{ page }}
      </button>

      <button
        type="button"
        class="evo-pagination-btn"
        [disabled]="!hasNextPage"
        aria-label="Próxima página"
        (click)="nextPage()"
      >
        <svg class="h-4 w-4" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M10 6 8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" />
        </svg>
      </button>
    </nav>
  `,
  styles: [
    `
      .evo-pagination-btn {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        min-width: 30px;
        height: 30px;
        padding: 0 0.25rem;
        border: 1px solid rgb(var(--aav-neutral-200));
        border-radius: var(--aav-radius-xs);
        background-color: rgb(var(--aav-surface));
        color: rgb(var(--aav-on-surface-variant));
        font-size: 13px;
        font-weight: 600;
        transition:
          background-color 0.15s ease,
          border-color 0.15s ease,
          color 0.15s ease;
      }

      .evo-pagination-btn:hover:not(:disabled) {
        border-color: rgb(var(--aav-primary));
        color: rgb(var(--aav-primary));
      }

      .evo-pagination-btn:disabled {
        color: rgb(var(--aav-neutral-200));
        cursor: not-allowed;
      }

      .evo-pagination-active,
      .evo-pagination-active:hover:not(:disabled) {
        border-color: rgb(var(--aav-primary));
        background-color: rgb(var(--aav-primary));
        color: rgb(var(--aav-on-primary));
        font-weight: 600;
      }
    `,
  ],
})
export class PaginationComponent implements OnChanges {
  @Input() currentPage = 1;
  @Input() totalPages = 0;

  @Output() pageChange = new EventEmitter<number>();

  readonly maxRenderedPages = 5;
  renderedPages: number[] = [];

  ngOnChanges(changes: SimpleChanges): void {
    this.clampCurrentPage();

    if (changes['totalPages']) {
      this.initPagesToRender();
    }

    if (changes['currentPage'] && !this.isPageInWindow(this.currentPage)) {
      this.initPagesToRender();
    }
  }

  private clampCurrentPage(): void {
    if (this.totalPages > 0 && this.currentPage > this.totalPages) {
      this.currentPage = this.totalPages;
    }
    if (this.currentPage < 1) {
      this.currentPage = 1;
    }
  }

  get hasNextPage(): boolean {
    return this.currentPage < this.totalPages;
  }

  nextPage(): void {
    if (this.currentPage >= this.totalPages) {
      return;
    }

    const target = this.currentPage + 1;

    if (this.shouldSlideForwardTo(this.currentPage) || this.shouldSlideForwardTo(target)) {
      this.calculatePagesToRenderNext();
    }

    this.pageChange.emit(target);
  }

  previousPage(): void {
    if (this.currentPage <= 1) {
      return;
    }

    const target = this.currentPage - 1;

    if (this.shouldSlideBackwardTo(this.currentPage)) {
      this.calculatePagesToRenderPrevious(this.currentPage);
    } else if (this.shouldSlideBackwardTo(target)) {
      this.calculatePagesToRenderPrevious(target);
    }

    this.pageChange.emit(target);
  }

  setPage(page: number): void {
    if (this.shouldSlideForwardTo(page)) {
      this.calculatePagesToRenderNext();
    } else if (this.shouldSlideBackwardTo(page)) {
      this.calculatePagesToRenderPrevious(page);
    }

    this.pageChange.emit(page);
  }

  private get lastRenderedPage(): number {
    return this.renderedPages[this.renderedPages.length - 1];
  }

  private initPagesToRender(): void {
    const total = Math.max(0, this.totalPages);

    if (total <= this.maxRenderedPages) {
      this.renderedPages = Array.from({ length: total }, (_, i) => i + 1);
      return;
    }

    let first = this.currentPage - (this.maxRenderedPages - 1);
    if (first < 1) {
      first = 1;
    }
    this.renderedPages = Array.from({ length: this.maxRenderedPages }, (_, i) => first + i);
  }

  private renderPages(modifier: number): void {
    this.renderedPages = this.renderedPages.map((page) => page + modifier);
  }

  private calculatePagesToRenderNext(): void {
    const newLast = this.lastRenderedPage + (this.maxRenderedPages - 1);
    const modifier =
      newLast <= this.totalPages
        ? this.maxRenderedPages - 1
        : this.totalPages - this.lastRenderedPage;
    this.renderPages(modifier);
  }

  private calculatePagesToRenderPrevious(page: number): void {
    const newFirst = page - (this.maxRenderedPages - 1);
    const modifier = newFirst >= 1 ? -(this.maxRenderedPages - 1) : -(page - 1);
    this.renderPages(modifier);
  }

  private shouldSlideForwardTo(page: number): boolean {
    return this.isLastPageOnRenderedPages(page) && this.totalPages > this.lastRenderedPage;
  }

  private shouldSlideBackwardTo(page: number): boolean {
    return this.isFirstPageOnRenderedPages(page) && this.renderedPages[0] > 1;
  }

  private isLastPageOnRenderedPages(page: number): boolean {
    return page === this.lastRenderedPage;
  }

  private isFirstPageOnRenderedPages(page: number): boolean {
    return page === this.renderedPages[0];
  }

  private isPageInWindow(page: number): boolean {
    return page >= this.renderedPages[0] && page <= this.lastRenderedPage;
  }
}
